import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Dashboard_2 component
 */
export class BlocksIconDashboard2 extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property()
  color: IconColorType;

  /**
   * Title
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property()
  size: IconSize;

  /**
   *
   * Append to the class name to pass classes (such as utility classes) into the internals
   */
  @property()
  styleModifier = '';

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span class="${componentClassName}" role="presentation">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <g clip-path="url(#clip0_21774_64)">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M0.964844 3.4284C0.964844 2.06742 2.06815 0.964111 3.42913 0.964111H20.572C21.933 0.964111 23.0363 2.06741 23.0363 3.4284V20.5713C23.0363 21.9323 21.933 23.0355 20.572 23.0355H3.42913C2.06816 23.0355 0.964844 21.9323 0.964844 20.5713V3.4284ZM3.42913 2.46411C2.89658 2.46411 2.46484 2.89585 2.46484 3.4284V20.5713C2.46484 21.1038 2.89657 21.5355 3.42913 21.5355H20.572C21.1046 21.5355 21.5363 21.1038 21.5363 20.5713V3.4284C21.5363 2.89582 21.1046 2.46411 20.572 2.46411H3.42913Z"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M5.25056 5.99983C5.25056 5.58561 5.58634 5.24983 6.00056 5.24983H9.42913C9.84334 5.24983 10.1791 5.58561 10.1791 5.99983C10.1791 6.41404 9.84334 6.74983 9.42913 6.74983H6.00056C5.58634 6.74983 5.25056 6.41404 5.25056 5.99983Z"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M5.25056 9.85697C5.25056 9.44275 5.58634 9.10697 6.00056 9.10697H12.0006C12.4148 9.10697 12.7506 9.44275 12.7506 9.85697C12.7506 10.2712 12.4148 10.607 12.0006 10.607H6.00056C5.58634 10.607 5.25056 10.2712 5.25056 9.85697Z"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M18.4365 11.3895C18.7735 11.6303 18.8516 12.0987 18.6109 12.4358L14.3251 18.4358C14.2054 18.6034 14.022 18.7144 13.8179 18.7427C13.6139 18.771 13.4072 18.7142 13.2463 18.5855L9.48479 15.5763L6.53089 18.5302C6.23799 18.823 5.76312 18.823 5.47023 18.5302C5.17733 18.2373 5.17733 17.7624 5.47023 17.4695L8.8988 14.0409C9.16903 13.7707 9.59923 13.7469 9.89765 13.9856L13.5649 16.9194L17.3903 11.5639C17.631 11.2268 18.0994 11.1488 18.4365 11.3895Z"
            />
          </g>
          <defs>
            <clipPath id="clip0_21774_64">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-dashboard2') === undefined) {
  customElements.define('cat-icon-dashboard2', BlocksIconDashboard2);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-dashboard2': BlocksIconDashboard2;
  }
}
